import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { productKeys } from "@/lib/query";

export interface Product {
  id: string;
  name: string;
  description: string | null;
  sku: string;
  priceCents: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductLineItem {
  orderItemId: string;
  orderId: string;
  quantity: number;
  unitPriceCents: number;
  orderStatus: string;
  orderCreatedAt: string;
  customerId: string;
  customerName: string;
}

interface ProductDetail extends Product {
  lineItems: ProductLineItem[];
}

interface ProductsListResponse {
  data: Product[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export function useProducts(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...productKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<ProductsListResponse> => {
      const res = await fetch(`/api/products?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw new Error("Failed to fetch products");
      return res.json();
    },
  });
}

export function useProduct(productId: string) {
  return useQuery({
    queryKey: productKeys.detail(productId),
    queryFn: async (): Promise<ProductDetail> => {
      const res = await fetch(`/api/products/${productId}`);
      if (!res.ok) throw new Error("Failed to fetch product");
      const json = await res.json();
      return json.data;
    },
  });
}

interface ProductInput {
  name: string;
  sku: string;
  priceCents: number;
  description?: string | null;
  status?: string;
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProductInput): Promise<Product> => {
      const res = await fetch("/api/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Failed to create product");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      toast.success("Product created");
    },
    onError: () => {
      toast.error("Failed to create product");
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      const res = await fetch(`/api/products/${productId}`, { method: "DELETE" });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      toast.success("Product deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete product");
    },
  });
}

export function useUpdateProduct(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: {
      name?: string;
      description?: string | null;
      sku?: string;
      priceCents?: number;
      status?: string;
    }) => {
      const res = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      toast.success("Product updated");
    },
    onError: () => {
      toast.error("Failed to update product");
    },
  });
}
